/*
 * Copyright © 2026 MS IMAGES. All rights reserved.
 * Proprietary and confidential — unauthorized copying, distribution, or use
 * of this file, via any medium, is strictly prohibited. See LICENSE.
 */

/**
 * ============================================================================
 * CodeInput Component
 * ============================================================================
 * 
 * 6-digit code input for MFA verification.
 * Features: auto-focus, paste support, backspace navigation.
 * 
 * ============================================================================
 */

import { useState, useRef, useEffect, useCallback } from 'react';

interface CodeInputProps {
  /** Number of digits */
  length?: number;
  /** Callback when all digits are entered */
  onComplete?: (code: string) => void;
  /** Callback on value change */
  onChange?: (code: string) => void;
  /** Whether the input is disabled */
  disabled?: boolean;
  /** Whether there's an error */
  error?: boolean;
  /** Auto-focus first input on mount */
  autoFocus?: boolean;
  /** Size variant */
  size?: 'sm' | 'md' | 'lg';
}

export function CodeInput({
  length = 6,
  onComplete,
  onChange,
  disabled = false,
  error = false,
  autoFocus = true,
  size = 'md',
}: CodeInputProps) {
  const [values, setValues] = useState<string[]>(Array(length).fill(''));
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);

  // Size variants - matching prototype: 48x56px, 24px font
  const sizeClasses = {
    sm: 'w-10 h-12 text-lg',
    md: 'w-12 h-14 text-2xl',
    lg: 'w-12 h-14 text-2xl',
  };

  // Auto-focus first input
  useEffect(() => {
    if (autoFocus && inputRefs.current[0]) {
      inputRefs.current[0].focus();
    }
  }, [autoFocus]);

  // Handle value change
  const handleChange = useCallback((index: number, value: string) => {
    // Only allow digits
    const digit = value.replace(/\D/g, '').slice(-1);
    
    const newValues = [...values];
    newValues[index] = digit;
    setValues(newValues);

    // Notify parent
    const code = newValues.join('');
    onChange?.(code);

    // Auto-focus next input
    if (digit && index < length - 1) {
      inputRefs.current[index + 1]?.focus();
    }

    // Check if complete
    if (newValues.every(v => v) && newValues.length === length) {
      onComplete?.(code);
    }
  }, [values, length, onChange, onComplete]);

  // Handle key down
  const handleKeyDown = useCallback((index: number, e: React.KeyboardEvent) => {
    // Backspace - move to previous
    if (e.key === 'Backspace') {
      if (!values[index] && index > 0) {
        inputRefs.current[index - 1]?.focus();
        const newValues = [...values];
        newValues[index - 1] = '';
        setValues(newValues);
        onChange?.(newValues.join(''));
      }
    }
    
    // Arrow keys
    if (e.key === 'ArrowLeft' && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
    if (e.key === 'ArrowRight' && index < length - 1) {
      inputRefs.current[index + 1]?.focus();
    }
  }, [values, length, onChange]);

  // Handle paste
  const handlePaste = useCallback((e: React.ClipboardEvent) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, length);
    
    if (pastedData) {
      const newValues = Array(length).fill('');
      pastedData.split('').forEach((char, i) => {
        if (i < length) newValues[i] = char;
      });
      setValues(newValues);
      
      const code = newValues.join('');
      onChange?.(code);
      
      // Focus last filled or first empty
      const lastIndex = Math.min(pastedData.length, length) - 1;
      inputRefs.current[lastIndex]?.focus();
      
      // Check if complete
      if (pastedData.length >= length) {
        onComplete?.(code);
      }
    }
  }, [length, onChange, onComplete]);

  // Reset function (can be called via ref)
  const reset = useCallback(() => {
    setValues(Array(length).fill(''));
    inputRefs.current[0]?.focus();
  }, [length]);

  return (
    <div className="code-input-container">
      <div className="code-input-wrapper">
        {values.map((value, index) => (
          <input
            key={index}
            ref={el => inputRefs.current[index] = el}
            type="text"
            inputMode="numeric"
            pattern="\d*"
            maxLength={1}
            value={value}
            onChange={e => handleChange(index, e.target.value)}
            onKeyDown={e => handleKeyDown(index, e)}
            onPaste={handlePaste}
            onFocus={e => e.target.select()}
            disabled={disabled}
            className={`
              code-input-digit
              ${sizeClasses[size]}
              ${error ? 'code-input-error' : ''}
              ${disabled ? 'code-input-disabled' : ''}
            `}
            aria-label={`Digit ${index + 1}`}
          />
        ))}
      </div>

      <style>{`
        .code-input-container {
          width: 100%;
        }

        .code-input-wrapper {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 8px;
        }

        .code-input-digit {
          width: 48px;
          height: 56px;
          font-family: var(--font-mono);
          font-size: 24px;
          font-weight: 600;
          text-align: center;
          background: rgba(13, 15, 20, 0.6);
          border: 2px solid rgba(255, 255, 255, 0.2);
          border-radius: var(--radius-lg);
          color: var(--color-text-primary);
          transition: all 150ms ease;
          caret-color: var(--color-brand-primary);
        }

        [data-theme="light"] .code-input-digit {
          background: #FFFFFF;
          border: 2px solid #CBD5E0;
          color: #1A1D29;
        }

        .code-input-digit:focus {
          outline: none;
          border-color: var(--color-brand-primary);
          box-shadow: 0 0 0 3px rgba(139, 0, 0, 0.15);
        }

        [data-theme="light"] .code-input-digit:focus {
          border-color: #1A5F7A;
          box-shadow: 0 0 0 3px rgba(26, 95, 122, 0.15);
        }

        .code-input-digit:hover:not(:focus):not(:disabled) {
          border-color: var(--input-border-hover);
        }

        .code-input-digit::placeholder {
          color: var(--input-placeholder);
        }

        .code-input-digit::selection {
          background: var(--color-brand-primary);
          color: white;
        }

        .code-input-error {
          border-color: var(--color-error) !important;
          animation: shake 0.3s ease-in-out;
        }

        .code-input-error:focus {
          box-shadow: 
            0 0 0 3px var(--color-error-light),
            0 0 20px rgba(220, 38, 38, 0.3);
        }

        .code-input-disabled {
          opacity: 0.5;
          cursor: not-allowed;
        }

        @keyframes shake {
          0%, 100% { transform: translateX(0); }
          25% { transform: translateX(-4px); }
          75% { transform: translateX(4px); }
        }

        /* Mobile responsiveness */
        @media (max-width: 400px) {
          .code-input-wrapper {
            gap: 4px;
          }

          .code-input-digit {
            width: 40px !important;
            height: 48px !important;
            font-size: 18px !important;
          }
        }
      `}</style>
    </div>
  );
}

export default CodeInput;
